'use client'

import { useParams, usePathname } from 'next/navigation'
import {
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  createContext,
  useContext,
  useEffect,
  useState,
} from 'react'
import { useEventListener, useMediaQuery } from 'usehooks-ts'

import Loading from '@/components/Loading'
import { useUAContext } from '@/context/UAContext'

type RouteChangeContextType = {
  isRouteLoading: boolean
  setIsRouteLoading: Dispatch<SetStateAction<boolean>>
}

const RouteChangeContext = createContext<RouteChangeContextType | undefined>(undefined)

// Event targets are not always Elements (Firefox dispatches mousemove on the Document while dragging)
function getTargetElement(target: EventTarget | null): Element | null {
  if (target instanceof Element) {
    return target
  }

  return target instanceof Node ? target.parentElement : null
}

export function RouteChangeProvider({ children }: PropsWithChildren) {
  const pathname = usePathname()
  const params = useParams()
  const [isRouteLoading, setIsRouteLoading] = useState(false)
  const { deviceType } = useUAContext()
  const isMobileOrTablet = useMediaQuery('(max-width: 1023px)') || deviceType !== 'desktop'

  const [isDragging, setIsDragging] = useState(false)

  useEventListener('click', (e) => {
    if (isDragging) {
      return
    }

    // 🥷🏼 Check if it's a Command + Click (open in new window) or Ctrl + Click
    if (e.metaKey || e.ctrlKey) {
      return
    }

    const link = getTargetElement(e.target)?.closest<HTMLAnchorElement>('a[href]')

    if (link?.target === '_blank') {
      return
    }

    if (link && !link.hash && link.hostname === window.location.hostname) {
      setIsRouteLoading(true)
    }
  })

  useEventListener('dragstart', () => {
    setIsDragging(true)
  })

  useEventListener('dragend', () => {
    setTimeout(() => setIsDragging(false), 100)
  })

  useEventListener('mousedown', (e) => {
    const link = getTargetElement(e.target)?.closest<HTMLAnchorElement>('a[href]')

    if (link) {
      link.setAttribute('data-potential-drag', 'true')
    }
  })

  useEventListener('mousemove', (e) => {
    if (e.buttons === 1) {
      const link = getTargetElement(e.target)?.closest('a[href][data-potential-drag]')

      if (link) {
        setIsDragging(true)
      }
    }
  })

  useEventListener('mouseup', () => {
    document.querySelectorAll('a[data-potential-drag]').forEach((link) => {
      link.removeAttribute('data-potential-drag')
    })
    setTimeout(() => setIsDragging(false), 100)
  })

  useEffect(() => {
    // 🥷🏼 after changed page, set isAppLoading to false
    // document.body.classList.remove('route-loading')
    setIsRouteLoading(false)
  }, [pathname, params])

  return (
    <RouteChangeContext.Provider value={{ isRouteLoading, setIsRouteLoading }}>
      {isMobileOrTablet && isRouteLoading && <Loading disableLoader />}
      {children}
    </RouteChangeContext.Provider>
  )
}

export const useRouteChangeContext = () => {
  const context = useContext(RouteChangeContext)
  if (context === undefined) {
    throw new Error('useRouteChangeContext must be used within a RouteChangeProvider')
  }
  return context
}
